import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { X, Phone, Mail, Users, MessageSquare, MapPin, FileText, CheckSquare, Edit3 } from 'lucide-react';
import { inrCompact } from '../../lib/format';
import ActivityTimeline from './ActivityTimeline';
import TaskFormFields from '../TaskFormFields';
import DocumentUploader, { ENQUIRY_DOC_CATEGORIES } from '../DocumentUploader';

const QUICK_ACTIONS = [
  { key: 'note', label: 'Note', icon: FileText, color: 'bg-slate-100 text-slate-700 hover:bg-slate-200' },
  { key: 'call', label: 'Call', icon: Phone, color: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' },
  { key: 'email', label: 'Email', icon: Mail, color: 'bg-teal-50 text-teal-700 hover:bg-teal-100' },
  { key: 'meeting', label: 'Meeting', icon: Users, color: 'bg-violet-50 text-violet-700 hover:bg-violet-100' },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageSquare, color: 'bg-teal-50 text-teal-700 hover:bg-teal-100' },
  { key: 'site_visit', label: 'Site Visit', icon: MapPin, color: 'bg-amber-50 text-amber-700 hover:bg-amber-100' },
  { key: 'task', label: 'Task', icon: CheckSquare, color: 'bg-teal-50 text-[#1F7A6E] hover:bg-teal-100' },
];

/**
 * Generic HubSpot-style detail drawer.
 * Props:
 *  - entityType: 'lead' | 'opportunity'
 *  - entityId: id of the record
 *  - onClose: function
 *  - onUpdated: callback after activity/task added (to refresh parent list)
 *  - onEdit: optional callback to open the edit form
 */
export default function LeadDetailDrawer({ entityType, entityId, onClose, onUpdated, onEdit }) {
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [communications, setCommunications] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activeAction, setActiveAction] = useState(null);
  const [form, setForm] = useState({ subject: '', notes: '', duration_minutes: '', description: '', due_at: '', owner: '', assigned_to: '', linked_entity: null });
  const [oppDocs, setOppDocs] = useState([]);

  const endpointBase = {
    lead: '/crm/leads',
    opportunity: '/crm/opportunities',
  }[entityType];

  const recordKey = { lead: 'lead', opportunity: 'opportunity' }[entityType];

  const load = async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const res = await apiGet(`${endpointBase}/${entityId}`);
      setRecord(res[recordKey]);
      setCommunications(res.communications || []);
      setTasks(res.tasks || []);
      // Fetch docs for opportunities (document upload support)
      if (entityType === 'opportunity') {
        const docsRes = await apiGet(`/documents/opportunity/${entityId}`);
        setOppDocs(docsRes.documents || []);
      }
    } catch (err) {
      toast.error(`Failed to load ${entityType}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [entityId, entityType]);

  const resetForm = () => {
    setActiveAction(null);
    setForm({ subject: '', notes: '', duration_minutes: '', description: '', due_at: '', owner: '', assigned_to: '', linked_entity: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (activeAction === 'task') {
        if (!form.description || !form.due_at) {
          toast.error('Description and due date are required');
          return;
        }
        await apiPost(`${endpointBase}/${entityId}/tasks`, {
          description: form.description,
          reminder_date: new Date(form.due_at).toISOString(),
          owner: form.owner || undefined,
          assigned_to: form.assigned_to || undefined,
          is_completed: false,
        });
        toast.success('Task created');
      } else {
        if (!form.subject) {
          toast.error('Subject is required');
          return;
        }
        await apiPost(`${endpointBase}/${entityId}/communications`, {
          type: activeAction,
          subject: form.subject,
          notes: form.notes,
          duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : undefined,
        });
        toast.success('Activity logged');
      }
      resetForm();
      load();
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const completeTask = async (taskId) => {
    try {
      await apiPut(`/crm/tasks/${taskId}/complete`);
      toast.success('Task completed');
      load();
      onUpdated && onUpdated();
    } catch (err) {
      toast.error('Failed to complete task');
    }
  };

  const titleByType = {
    lead: record?.client,
    opportunity: record?.project_name,
  };
  const subtitleByType = {
    lead: record?.contact_person || record?.email || record?.phone || '—',
    opportunity: record?.client,
  };

  if (!entityId) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="lead-detail-drawer">
      <button
        type="button"
        className="flex-1 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
        data-testid="drawer-backdrop"
        aria-label="Close drawer"
      />
      <div className="w-full max-w-[920px] bg-slate-50 border-l border-slate-200 flex flex-col shadow-2xl">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-1">
              {entityType.replace('_', ' ')} · {record?.stage || record?.status || ''}
            </div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight truncate">
              {titleByType[entityType] || 'Loading…'}
            </h2>
            <div className="text-sm text-slate-600 mt-0.5 truncate">{subtitleByType[entityType]}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {onEdit && record && (
              <button
                onClick={() => onEdit(record)}
                className="px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider border border-slate-300 hover:border-[#1F7A6E] hover:text-[#1F7A6E] rounded-sm flex items-center gap-1"
                data-testid="drawer-edit-btn"
              >
                <Edit3 size={12} /> Edit
              </button>
            )}
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center text-slate-500 hover:text-[#0F172A] hover:bg-slate-100 rounded-sm"
              data-testid="drawer-close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {loading || !record ? (
          <div className="flex-1 flex items-center justify-center text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-[280px_1fr]">
            {/* Left: Contact / record info card */}
            <aside className="bg-white border-r border-slate-200 overflow-y-auto p-5 space-y-4">
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">About</div>
                <div className="space-y-2 text-sm">
                  {entityType === 'lead' && (
                    <>
                      <Field label="Contact" value={record.contact_person} />
                      <Field label="Email" value={record.email} />
                      <Field label="Phone" value={record.phone} />
                      <Field label="Industry" value={record.industry} />
                      <Field label="Location" value={record.location} />
                      <Field label="Source" value={record.source} />
                      <Field label="Project Type" value={record.project_type} />
                    </>
                  )}
                  {entityType === 'opportunity' && (
                    <>
                      <Field label="Client" value={record.client} />
                      <Field label="Probability" value={record.probability ? `${record.probability}%` : null} />
                      <Field label="Expected Close" value={record.expected_close_date ? new Date(record.expected_close_date).toLocaleDateString() : null} />
                    </>
                  )}
                  <Field label="Value" value={record.estimated_value ? inrCompact(record.estimated_value) : null} />
                  <Field label="Owner" value={record.owner} />
                  {record.priority && (
                    <Field label="Priority" value={record.priority} />
                  )}
                </div>
              </div>

              {record.notes && (
                <div>
                  <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">Notes</div>
                  <p className="text-xs text-slate-700 whitespace-pre-line bg-slate-50 border border-slate-200 rounded-sm p-3">{record.notes}</p>
                </div>
              )}

              <div className="pt-2 border-t border-slate-200 text-[11px] text-slate-500 space-y-1">
                <div>Created {record.created_at ? new Date(record.created_at).toLocaleDateString() : '—'}</div>
                {record.created_by && <div>by {record.created_by}</div>}
              </div>

              {/* Document upload — opportunities only */}
              {entityType === 'opportunity' && (
                <div>
                  <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">
                    Documents ({oppDocs.length})
                  </div>
                  <DocumentUploader
                    entityType="opportunity"
                    entityId={entityId}
                    documents={oppDocs}
                    categories={ENQUIRY_DOC_CATEGORIES}
                    onChange={(updated) => {
                      setOppDocs(updated);
                      if (onUpdated) onUpdated();
                    }}
                    testidPrefix="opp-doc"
                    title=""
                  />
                </div>
              )}
            </aside>

            {/* Right: Activity stream + quick log */}
            <main className="overflow-y-auto p-5 space-y-5">
              {/* Quick log toolbar */}
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">Log Activity / Create Task</div>
                <div className="flex flex-wrap gap-2">
                  {QUICK_ACTIONS.map(a => {
                    const Icon = a.icon;
                    const active = activeAction === a.key;
                    return (
                      <button
                        key={a.key}
                        onClick={() => setActiveAction(active ? null : a.key)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-display font-semibold border transition-colors ${
                          active ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : `${a.color} border-transparent`
                        }`}
                        data-testid={`quick-action-${a.key}`}
                      >
                        <Icon size={13} /> {a.label}
                      </button>
                    );
                  })}
                </div>

                {activeAction && (
                  <form onSubmit={handleSubmit} className="mt-4 pt-4 border-t border-slate-200 space-y-3" data-testid={`activity-form-${activeAction}`}>
                    {activeAction === 'task' ? (
                      <TaskFormFields
                        value={form}
                        onChange={setForm}
                        hideLinkedEntity
                        testIdPrefix="task"
                      />
                    ) : (
                      <>
                        <div>
                          <label className="label">Subject *</label>
                          <input
                            type="text"
                            value={form.subject}
                            onChange={e => setForm({ ...form, subject: e.target.value })}
                            className="input"
                            placeholder={`${activeAction === 'note' ? 'Note title' : `${QUICK_ACTIONS.find(q => q.key === activeAction)?.label} subject`}`}
                            required
                            data-testid="activity-subject-input"
                          />
                        </div>
                        <div>
                          <label className="label">Details</label>
                          <textarea
                            value={form.notes}
                            onChange={e => setForm({ ...form, notes: e.target.value })}
                            className="input"
                            rows={4}
                            placeholder="What was discussed? Next steps?"
                            data-testid="activity-notes-input"
                          />
                        </div>
                        {(activeAction === 'call' || activeAction === 'meeting') && (
                          <div className="w-40">
                            <label className="label">Duration (min)</label>
                            <input
                              type="number"
                              value={form.duration_minutes}
                              onChange={e => setForm({ ...form, duration_minutes: e.target.value })}
                              className="input"
                              data-testid="activity-duration-input"
                            />
                          </div>
                        )}
                      </>
                    )}
                    <div className="flex gap-2 justify-end pt-1">
                      <button type="button" onClick={resetForm} className="btn-secondary text-xs">Cancel</button>
                      <button type="submit" className="btn-primary text-xs" data-testid="activity-submit-btn">
                        {activeAction === 'task' ? 'Create Task' : 'Log Activity'}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Activity timeline */}
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">
                  Activity ({communications.length + tasks.length})
                </div>
                <ActivityTimeline
                  communications={communications}
                  tasks={tasks}
                  onCompleteTask={completeTask}
                />
              </div>
            </main>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-2 text-xs">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="font-medium text-[#0F172A] text-right truncate">{value}</span>
    </div>
  );
}
